/**
 * ArenaPanel — a 1v1 damage-pool PvP arena.
 *
 * Enter the queue, get matched to another queued player, then rally
 * damage with Strike (3s cooldown) over a 60-second window. Higher
 * damage at the bell wins ±25 rating; loser drops 15. Draws are neutral.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Swords, Zap, Trophy, Clock, ShieldOff, Crown, Users, LogOut,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { flagUrl } from "@/lib/utils";
import { useOpenPlayer } from "@/components/game/PlayerCard";
import { ITEM_ICON } from "@/lib/gameData";

const SLOT_ORDER = [
  { id: "head",    label: "Head"    },
  { id: "cape",    label: "Cape"    },
  { id: "neck",    label: "Neck"    },
  { id: "weapon",  label: "Weapon"  },
  { id: "body",    label: "Body"    },
  { id: "shield",  label: "Shield"  },
  { id: "legs",    label: "Legs"    },
  { id: "gloves",  label: "Gloves"  },
  { id: "boots",   label: "Boots"   },
  { id: "ring",    label: "Ring"    },
  { id: "ammo",    label: "Ammo"    },
];

function CombatantCard({ side, name, iso_a2, combat_level, equipment, damageTaken, isYou, onOpenProfile }) {
  const items = useGame().content?.items || {};
  return (
    <div
      className={
        "flex-1 min-w-0 panel-inset p-3 sm:p-4 space-y-2 " +
        (side === "left" ? "text-left" : "text-right")
      }
      data-testid={`arena-card-${isYou ? "you" : "foe"}`}
    >
      <div className={"flex items-center gap-2 " + (side === "right" ? "flex-row-reverse" : "")}>
        <div className={
          "w-12 h-12 rounded-lg border-2 flex items-center justify-center font-display text-xl shrink-0 " +
          (isYou ? "border-emerald-700 text-emerald-300 bg-emerald-950/40" : "border-red-800 text-red-300 bg-red-950/30")
        }>
          {(name || "?").slice(0, 2).toUpperCase()}
        </div>
        <button
          onClick={onOpenProfile}
          className="min-w-0 hover:opacity-80 transition-opacity"
        >
          <div className={
            "text-[10px] font-numeric uppercase tracking-widest " +
            (isYou ? "text-emerald-400" : "text-red-400")
          }>
            {isYou ? "You" : "Foe"}
          </div>
          <div className="font-display text-lg heading-gilded truncate flex items-center gap-1.5">
            {name}
            {iso_a2 && (
              <img
                src={flagUrl(iso_a2, 20)}
                alt=""
                className="w-4 h-3 object-cover rounded-[1px]"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
          </div>
          <div className="text-[11px] font-numeric text-stone-400">Combat Lv. <span className="text-amber-300">{combat_level ?? "?"}</span></div>
        </button>
      </div>

      {/* Equipment grid — 4×3 tight paperdoll */}
      <div className={"grid grid-cols-4 gap-1 " + (side === "right" ? "direction-rtl" : "")}>
        {SLOT_ORDER.map((s) => {
          const id = equipment?.[s.id];
          const it = id ? items[id] : null;
          return (
            <div
              key={s.id}
              title={it ? it.name : s.label}
              className={
                "aspect-square rounded border flex items-center justify-center text-lg " +
                (it ? "bg-stone-900 border-amber-800/70 text-amber-200" : "bg-stone-950/60 border-stone-800 text-stone-700")
              }
              data-testid={`arena-slot-${isYou ? "you" : "foe"}-${s.id}`}
            >
              {it ? (ITEM_ICON[id] || "◇") : (
                <span className="text-[8px] font-numeric uppercase tracking-wider text-stone-600">{s.label.slice(0,3)}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Damage taken bar */}
      <div>
        <div className="text-[10px] font-numeric uppercase tracking-widest text-stone-500 mb-1">
          Damage taken
        </div>
        <div className="h-2 rounded bg-stone-900 overflow-hidden">
          <div
            className={"h-full transition-all " + (isYou ? "bg-emerald-600" : "bg-red-700")}
            style={{ width: `${Math.min(100, damageTaken)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function Countdown({ endsAt }) {
  const [t, setT] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setT(Date.now()), 250); return () => clearInterval(i); }, []);
  const rem = Math.max(0, new Date(endsAt).getTime() - t);
  const s = Math.floor(rem / 1000);
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-numeric text-amber-300 bg-stone-950 border border-amber-800 rounded px-1.5 py-0.5">
      <Clock size={11} /> 0:{s.toString().padStart(2, "0")}
    </span>
  );
}

export function ArenaPanel() {
  const { snap } = useGame();
  const openPlayer = useOpenPlayer();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [strikeCooldownUntil, setStrikeCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 250); return () => clearInterval(i); }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/arena/status");
      setState(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, state?.active_duel ? 1500 : 4000);
    return () => clearInterval(iv);
  }, [load, state?.active_duel]);

  const queue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/game/arena/queue");
      if (data.matched) toast.success("Match found — the bell rings!");
      else toast.success("Queued for a duel.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await api.post("/api/game/arena/cancel"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const strike = async () => {
    if (!state?.active_duel) return;
    if (Date.now() < strikeCooldownUntil) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/game/arena/duel/${state.active_duel.id}/strike`);
      toast.success(`Strike +${data.damage}`);
      setStrikeCooldownUntil(Date.now() + (state?.strike_cooldown_s ?? 3) * 1000);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const myId = snap?.user_id;
  const duel = state?.active_duel;
  const iAmA = duel && duel.a_id === myId;
  const me   = duel ? (iAmA ? { name: duel.a_name, dmg: duel.damage.a } : { name: duel.b_name, dmg: duel.damage.b }) : null;
  const foe  = duel ? (iAmA ? { id: duel.b_id, name: duel.b_name, dmg: duel.damage.b } : { id: duel.a_id, name: duel.a_name, dmg: duel.damage.a }) : null;
  const total = duel ? Math.max(1, me.dmg + foe.dmg) : 1;
  const myPct = duel ? Math.round((me.dmg / total) * 100) : 50;

  const cdRemainingMs = Math.max(0, strikeCooldownUntil - now);
  const cdSec = Math.ceil(cdRemainingMs / 1000);

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="arena-panel">
      {/* Header */}
      <div className="panel overflow-hidden">
        <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-lg border-2 border-red-800 bg-stone-950 flex items-center justify-center">
            <Swords size={30} className="text-red-400" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-4xl heading-gilded">The Arena</h1>
            <p className="text-stone-400 font-body italic text-xs sm:text-sm">
              Sixty seconds of steel. Two names on the sand. Only one leaves standing.
            </p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs sm:text-sm font-numeric text-stone-300">
              <span>Rating · <span className="text-amber-300" data-testid="arena-rating">{state?.rating ?? "…"}</span></span>
              <span>Recent · <span className="text-emerald-400">{state?.recent_wins ?? 0}W</span> · <span className="text-red-400">{state?.recent_losses ?? 0}L</span></span>
              <span>In queue · <span className="text-amber-300">{state?.queue_size ?? 0}</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Duel arena */}
      {duel ? (
        <div className="panel p-4 sm:p-6 space-y-4" data-testid="arena-active-duel">
          <div className="flex items-center justify-between">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-red-400 flex items-center gap-1">
              <Swords size={12} /> Duel in progress
            </h3>
            <Countdown endsAt={duel.ends_at} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
            <CombatantCard
              side="left"
              name={iAmA ? duel.a_name : duel.b_name}
              iso_a2={iAmA ? duel.a_iso_a2 : duel.b_iso_a2}
              combat_level={iAmA ? duel.a_combat_level : duel.b_combat_level}
              equipment={iAmA ? duel.a_equipment : duel.b_equipment}
              damageTaken={Math.round((foe.dmg / total) * 100)}
              isYou
              onOpenProfile={() => openPlayer(myId)}
            />
            <div className="hidden md:flex items-center justify-center text-stone-600 font-display text-3xl">vs</div>
            <CombatantCard
              side="right"
              name={foe.name}
              iso_a2={iAmA ? duel.b_iso_a2 : duel.a_iso_a2}
              combat_level={iAmA ? duel.b_combat_level : duel.a_combat_level}
              equipment={iAmA ? duel.b_equipment : duel.a_equipment}
              damageTaken={Math.round((me.dmg / total) * 100)}
              isYou={false}
              onOpenProfile={() => openPlayer(foe.id)}
            />
          </div>

          <button
            onClick={strike}
            disabled={busy || cdRemainingMs > 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded font-body text-lg bg-red-800 hover:bg-red-700 text-stone-100 disabled:bg-stone-900 disabled:text-stone-500 transition-colors"
            data-testid="arena-strike"
          >
            <Swords size={16} /> {cdRemainingMs > 0 ? `Ready in ${cdSec}s` : `Attack ${foe.name}`}
          </button>
        </div>
      ) : state?.in_queue ? (
        <div className="panel p-6 text-center" data-testid="arena-queue-waiting">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-950/30 border border-amber-800 mb-3 animate-pulse">
            <Users size={22} className="text-amber-400" />
          </div>
          <div className="font-display text-lg heading-gilded">Waiting for an opponent</div>
          <div className="text-stone-500 font-body text-sm mt-1">{state.queue_size} in queue · a challenger is on the way.</div>
          <button
            onClick={cancel}
            disabled={busy}
            className="mt-4 text-xs font-body px-3 py-1.5 rounded border border-stone-700 text-stone-400 hover:text-red-300 hover:border-red-800 inline-flex items-center gap-1"
            data-testid="arena-leave-queue"
          >
            <LogOut size={12} /> Leave queue
          </button>
        </div>
      ) : (
        <div className="panel p-6 text-center" data-testid="arena-idle">
          <div className="font-display text-lg heading-gilded">Ready to test your steel?</div>
          <div className="text-stone-500 font-body text-sm mt-1">
            Queue for a 60-second damage-pool duel. Higher damage wins — rating adjusts ±25 / ∓15.
          </div>
          <button
            onClick={queue}
            disabled={busy}
            className="mt-4 btn-gold px-5 py-2 inline-flex items-center gap-2 font-body"
            data-testid="arena-queue"
          >
            <Swords size={14} /> Enter the Arena
          </button>
        </div>
      )}

      {/* Recent duels */}
      {state?.recent?.length > 0 && (
        <div className="panel p-4">
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2">Your recent duels</h3>
          <div className="space-y-1">
            {state.recent.map((r) => {
              const iAmA = r.a_id === myId;
              const you  = iAmA ? r.a_name : r.b_name;
              const foe  = iAmA ? r.b_name : r.a_name;
              const won  = r.winner_id === myId;
              const draw = !r.winner_id;
              return (
                <div key={r.id} className="flex items-center justify-between text-xs font-body py-1" data-testid={`arena-recent-${r.id}`}>
                  <span className="text-stone-300 truncate">
                    {you} <span className="text-stone-600">vs</span> <span className="text-amber-300">{foe}</span>
                  </span>
                  <span className={
                    "font-numeric uppercase tracking-widest text-[10px] " +
                    (draw ? "text-stone-500" : won ? "text-emerald-300" : "text-red-400")
                  }>
                    {draw ? "Draw" : won ? "Victory" : "Defeat"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
